import { addNotification, useDispatch, useSelector } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, size } from 'lodash'
import { setLocatorToken } from '@ggs/store/actions/session'
import { useApiRequest } from './'

/**
 * @typedef {import("../../definitions").DealerListResponse} DealerListResponse
 */

/**
 * @typedef {import("../../definitions").RepResponse} RepResponse
 */

/**
 @typedef {(pathParams: string) => Promise<DealerListResponse>} GetDealers
 */

/**
 @typedef {(pathParams: string) => Promise<RepResponse>} GetReps
 */


/**
 * @typedef {Object} LocatorApiHook
 * @property {GetReps} getReps
 */

/**
 * Hook that expose utilities to retrieve locator resources.
 * @return {LocatorApiHook}
 */
function useLocatorApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl = `${process.env.REACT_APP_LOCATOR_API_BASE_URL}` || 'https://as.ddev.site/api/replocator'
  const basePath = ''
  const authToken = ''
  const drupalToken = useSelector((state) => state.session.drupalToken)
  const { doApiRequest } = useApiRequest({
    baseUrl,
    authToken,
    csrfToken: drupalToken,
  })

  /**
   * Acquire a Drupal session token.
   */
  // const getApiToken = async () => {
  //   const endpoint = 'Locations/authenticate'
  //   const response = await doRequest(endpoint, {})
  //   return response?.token || ''
  // }

  /**
   * Negotiate to get a Locator API token.
   * @return {Promise<void>}
   */
  // const acquireLocatorApiToken = async () => {
  //   const token = await getApiToken()
  //   const rawToken = token.toString()
  //   if (size(rawToken) > 10) {
  //     // Save token at Redux, however we will not use it for now due to expiration TTL is very low.
  //     dispatch(setLocatorToken(token.toString()))
  //
  //     return rawToken
  //   }
  // }

  /**
   * Process a Locator API endpoint request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @return {Promise<any>}
   */
  const doRequest = async (endpoint, data = null, customHeaders, method = '') => {
    const response = doApiRequest({
      endpoint: encodeURIComponent(endpoint),
      data,
      customHeaders,
      method,
      extraOptions: {},
    })

    const errorCode = get(response, 'errorCode', null)
    if (errorCode) {
      // Session is valid but user don't have privilege granted for the consumed endpoint.
      dispatch(
        addNotification(
          'locator_api_request_failed',
          'error',
          t('Locator API failed to process the request.')
        )
      )

      return null
    }

    return response
  }

  /**
   * Get dealers records.
   * @type {GetDealers}
   * @param {string=} pathParams The path params to be used in the request.
   * @return {Promise<DealerListResponse>}
   */
  const getDealers = async (pathParams = '') => {
    const endpoint = `${basePath}/dealers`
    const extraParams = pathParams ? `/${pathParams}` : ''

    // We need to acquire a token for each request due to the token TTL is very low and when using same
    // token for 1 minute is producing errors.
    // const locatorToken = await acquireLocatorApiToken()
    // const authorizationHeader = `Bearer ${locatorToken}`
    // const customHeaders = {
    //   Authorization: authorizationHeader,
    // }

    const response = await doRequest(endpoint + extraParams, null)

    if (!response) {
      return null
    }

    return response
  }

  const getReps = async (pathParams = '') => {
    const endpoint = `${basePath}`
    const extraParams = pathParams ? `/${pathParams}` : ''

    // We need to acquire a token for each request due to the token TTL is very low and when using same
    // token for 1 minute is producing errors.
    // const locatorToken = await acquireLocatorApiToken()
    // const authorizationHeader = `Bearer ${locatorToken}`
    // const customHeaders = {
    //   Authorization: authorizationHeader,
    // }

    const response = await doRequest(endpoint + extraParams, null)

    if (!response) {
      return null
    }

    return response
  }

  return {
    getReps,
  }
}

export default useLocatorApi
